import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { LIVE_PLATFORM_LIST, PLATFORM_SPECS } from '../../../shared/platforms';
import { PlatformIcon } from '../ui';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '../../lib/utils';

const HIGHLIGHTS = [
  {
    title: 'Write once, land everywhere',
    body: 'One set of notes becomes a post tuned to each platform — length, tone, hashtags and all.',
  },
  {
    title: 'In your voice, not a robot’s',
    body: 'Paste a few things you have written and every draft comes back sounding like you.',
  },
  {
    title: 'Schedule and forget',
    body: 'Pick a time per platform. Publishing, retries and failure alerts are handled for you.',
  },
] as const;

export interface AuthLayoutProps {
  /** Small line above the title, e.g. "First run". */
  eyebrow?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Rendered under the card, outside its border. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Signed-out shell for /login, /setup and /accept-invite: a brand panel that
 * says what the product does, and a focused card for the one thing to do next.
 */
export function AuthLayout({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  className,
}: AuthLayoutProps) {
  return (
    <div className="pap-aurora relative flex min-h-dvh flex-col lg:flex-row">
      <div className="pap-dotfield pointer-events-none absolute inset-0" aria-hidden />

      {/* Brand panel */}
      <aside className="relative hidden w-[46%] max-w-2xl shrink-0 flex-col justify-between border-r border-border bg-surface/40 px-12 py-12 lg:flex">
        <div className="flex items-center gap-2.5">
          <span
            className="flex size-9 items-center justify-center rounded-lg bg-accent text-accent-fg"
            aria-hidden
          >
            <Sparkles className="size-4.5" />
          </span>
          <span>
            <span className="block text-sm font-semibold tracking-tight text-fg">Post Across</span>
            <span className="block text-[11px] text-subtle">Platforms</span>
          </span>
        </div>

        <div className="max-w-md space-y-8">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight text-fg">
            Compose once.
            <br />
            Publish everywhere.
          </h2>
          <ul className="space-y-5">
            {HIGHLIGHTS.map((highlight, index) => (
              <motion.li
                key={highlight.title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * index + 0.1, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-1"
              >
                <p className="text-sm font-medium text-fg">{highlight.title}</p>
                <p className="text-sm leading-relaxed text-muted">{highlight.body}</p>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-subtle">
            Publishing to
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {LIVE_PLATFORM_LIST.map((spec) => (
              <PlatformIcon key={spec.id} platform={spec.id} size="md" variant="soft" />
            ))}
            <span className="text-xs text-subtle">
              {PLATFORM_SPECS.tiktok.shortName} and {PLATFORM_SPECS.youtube.shortName} coming soon
            </span>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="relative flex flex-1 flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-5 lg:justify-end lg:px-10">
          <span className="flex items-center gap-2 lg:hidden">
            <span
              className="flex size-8 items-center justify-center rounded-lg bg-accent text-accent-fg"
              aria-hidden
            >
              <Sparkles className="size-4" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-fg">Murmur</span>
          </span>
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center justify-center px-5 pb-16 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={cn('w-full max-w-md', className)}
          >
            <div className="space-y-2 pb-6">
              {eyebrow && (
                <div className="text-[11px] font-medium uppercase tracking-wide text-accent">
                  {eyebrow}
                </div>
              )}
              <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
              {subtitle && <p className="text-sm leading-relaxed text-muted">{subtitle}</p>}
            </div>

            <div className="pap-card p-6 shadow-pop">{children}</div>

            {footer && <div className="pt-5 text-center text-xs text-muted">{footer}</div>}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
